/**
 * After a production deploy, hashed asset URLs change. Existing tabs can hit
 * failed dynamic imports, Vite preload errors, or React Router route errors.
 * This module reloads the tab once so the user picks up the new index + chunks.
 */

const SESSION_ATTEMPTS_KEY = 'platform_chunk_recovery_attempts'
const MAX_ATTEMPTS = 2

/** Matches `src/styles/theme/typography.ts` (fonts loaded via global `fonts.css`). */
const FONT_FAMILY =
	'"Gotham", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji"'

export function isLikelyChunkOrDeployLoadError(reason: unknown): boolean {
	if (reason == null) {
		return false
	}
	const name = typeof reason === 'object' && reason !== null && 'name' in reason ? String((reason as Error).name) : ''
	if (name === 'ChunkLoadError') {
		return true
	}
	const msg =
		typeof reason === 'string'
			? reason
			: reason instanceof Error
				? reason.message
				: String((reason as { message?: string }).message ?? reason)
	const m = msg.toLowerCase()
	return (
		m.includes('failed to fetch dynamically imported module') ||
		m.includes('error loading dynamically imported module') ||
		m.includes('importing a module script failed') ||
		m.includes('failed to import module script') ||
		m.includes('expected a javascript-or-wasm module script') ||
		(m.includes('404') && (m.includes('.js') || m.includes('script')))
	)
}

let reloadAlreadyScheduled = false

const OVERLAY_ID = 'platform-deploy-recovery-overlay'

/**
 * Same messaging as `/errors/site-updated`, but inlined so we do **not** dynamic-import
 * another route chunk (which can also 404 right after a deploy).
 */
function showSiteUpdatedOverlay(): void {
	if (typeof document === 'undefined' || document.getElementById(OVERLAY_ID)) {
		return
	}
	const root = document.createElement('div')
	root.id = OVERLAY_ID
	root.setAttribute('role', 'status')
	root.setAttribute('aria-live', 'polite')
	Object.assign(root.style, {
		position: 'fixed',
		inset: '0',
		zIndex: '2147483647',
		background: '#fff',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		fontFamily: FONT_FAMILY,
		padding: '24px',
		textAlign: 'center',
		boxSizing: 'border-box',
	})
	root.innerHTML = `
		<div>
			<h1 style="font-size:1.5rem;font-weight:600;margin:0 0 8px;color:#1a1a1a">The site has been updated</h1>
			<p style="color:#666;margin:0;font-size:1rem;line-height:1.5">The page will reload shortly.</p>
		</div>
	`
	document.body.appendChild(root)
}

export interface ReloadForNewDeploymentOptions {
	/** `true` (default): brief “site updated” fullscreen message, then reload. `false`: reload immediately with no overlay. */
	overlay?: boolean
}

export function reloadForNewDeployment(options?: ReloadForNewDeploymentOptions): void {
	if (reloadAlreadyScheduled) {
		return
	}
	reloadAlreadyScheduled = true

	try {
		const n = Number(sessionStorage.getItem(SESSION_ATTEMPTS_KEY) || '0')
		if (n >= MAX_ATTEMPTS) {
			// eslint-disable-next-line no-console -- avoid silent infinite reload loops
			console.error('[platform] Stopped automatic reload after repeated chunk load failures.')
			reloadAlreadyScheduled = false
			return
		}
		sessionStorage.setItem(SESSION_ATTEMPTS_KEY, String(n + 1))
	} catch {
		// private mode / storage blocked — still try one reload
	}

	const useOverlay = options?.overlay !== false

	const doReload = () => {
		window.location.reload()
	}

	if (useOverlay) {
		showSiteUpdatedOverlay()
		// One frame (or two) so the overlay can paint before navigation tears down the document.
		window.setTimeout(doReload, 64)
	} else {
		doReload()
	}
}

/** Successful load: allow future automatic recoveries. */
function clearRecoveryAttemptsAfterIdle(): void {
	window.addEventListener('load', () => {
		window.setTimeout(() => {
			try {
				sessionStorage.removeItem(SESSION_ATTEMPTS_KEY)
			} catch {
				// ignore
			}
		}, 8000)
	})
}

let registered = false

/**
 * Register before React mounts so the first navigation after a deploy is covered.
 */
export function registerDeployChunkRecovery(): void {
	if (registered) {
		return
	}
	registered = true
	clearRecoveryAttemptsAfterIdle()

	window.addEventListener('vite:preloadError', (event) => {
		event.preventDefault()
		// eslint-disable-next-line no-console -- deploy visibility
		console.warn('[platform] vite:preloadError — reloading for new deployment.')
		reloadForNewDeployment()
	})

	window.addEventListener('unhandledrejection', (event) => {
		if (!isLikelyChunkOrDeployLoadError(event.reason)) {
			return
		}
		event.preventDefault()
		// eslint-disable-next-line no-console -- deploy visibility
		console.warn('[platform] Unhandled chunk/dynamic import failure — reloading.', event.reason)
		reloadForNewDeployment()
	})

	if (import.meta.env.DEV) {
		;(window as unknown as { __reloadForNewDeployment: typeof reloadForNewDeployment }).__reloadForNewDeployment =
			reloadForNewDeployment
	}
}
